import AgreementItem from '@modules/Edo/Agreement';
import AgreementCS from '@modules/Edo/AgreementsCS/AgreementsCS';
import EdoAttributeForm from '@modules/Edo/EdoAttribute/EdoAttributeForm';
import EdoRowColumnForm from '@modules/Edo/EdoRowColumn/EdoRowColumnForm';
import EdoUploadForm from '@modules/Edo/EdoUploadForm';
import EdoRemark from '@modules/Edo/EdoRemark';
import { useOutletContext } from 'react-router-dom';
import { Form } from 'antd';
import useAuth from '@hooks/useAuth';

import { ContextType } from '.';
import { useEffect, useMemo, useState } from 'react';
import EdoMatching from '@modules/Edo/EdoMathcing/EdoMatching';
import CreateOrderDoc from '@modules/Edo/CreateOrderDoc/CreateOrderDoc';
import type { ColleaguesListType } from '@hooks/useColleagues';

/** Собирает все id пользователей из дерева colleaguesList */
function collectUserIds(list: ColleaguesListType): Set<number> {
  const ids = new Set<number>();
  function walk(items: ColleaguesListType) {
    items.forEach((item) => {
      item.users?.forEach((u) => ids.add(u.id));
      if (item.child?.length) walk(item.child);
    });
  }
  walk(list);
  return ids;
}

/**
 * Добавляем в список коллег пользователей из атрибутов документа (АДРЕСАТ, ИСПОЛНИТЕЛЬ),
 * которых нет в основном дереве (например неактивных). Иначе TreeSelect показывает id вместо ФИО.
 */
function mergeColleaguesWithAttributeUsers(
  colleaguesList: ColleaguesListType,
  attributes: any[] | undefined
): ColleaguesListType {
  if (!attributes?.length) return colleaguesList;
  const existingIds = collectUserIds(colleaguesList);
  const subjectAttrs = attributes.filter(
    (a) =>
      a?.attribute_type?.text_code === 'SUBJECT' ||
      a?.attribute_type?.text_code === 'SUBJECT_SHORT'
  );
  const usersById = new Map<number, any>();
  subjectAttrs.forEach((a) => {
    const v = a.number_value;
    if (
      v &&
      typeof v === 'object' &&
      v.id != null &&
      !existingIds.has(v.id) &&
      !usersById.has(v.id)
    ) {
      usersById.set(v.id, {
        id: v.id,
        full_name: v.full_name ?? '',
        short_name: v.short_name ?? v.full_name ?? '',
        client_id: v.client_id,
        foreign_id: v.foreign_id ?? 0,
        first_name: v.first_name ?? '',
        last_name: v.last_name ?? '',
        parent_name: v.parent_name ?? '',
        avatar: v.avatar,
      });
    }
  });
  const usersFromDoc = Array.from(usersById.values());
  if (usersFromDoc.length === 0) return colleaguesList;
  const extraNode = {
    id: -1,
    full_name: 'Из документа',
    users: usersFromDoc,
    child: [] as ColleaguesListType,
    client_id: undefined,
    foreign_id: 0,
    isn: 0,
  };
  return [...colleaguesList, extraNode];
}

function EdoDocumentPage() {
  const { docEdo, settings, colleaguesList, handbookList, departmentList } =
    useOutletContext<ContextType>();

  const colleaguesListForAttributes = useMemo(
    () =>
      mergeColleaguesWithAttributeUsers(
        colleaguesList,
        docEdo?.data?.attributes
      ),
    [colleaguesList, docEdo?.data?.attributes]
  );

  const [form] = Form.useForm();
  const [matchingForm] = Form.useForm();
  const auth = useAuth();
  const [activeAgreementsCS, setActiveAgreementsCS] = useState<boolean>(false);
  const findTd = Object.keys(auth?.user?.data?.permission?.access_role).filter(
    (key) =>
      auth?.user?.data?.permission?.access_role[key].slug.toLowerCase() === 'td'
  );
  const isAdmin = findTd.length > 0 || auth?.user?.data?.permission?.is_admin;

  const isDisabledForm = docEdo.data?.stage_id
    ? docEdo.data?.stage_id != 1
    : docEdo.data?.stage?.slug !== 'in_work' || !docEdo.data.active;

  /**
   * Прописываем Title документа в виде ИмяДокумента ТипДокумента
   */
  useEffect(() => {
    const docType = docEdo?.data?.class?.full_name;
    const docName = docEdo?.data?.document_id;

    if (docType && docName) {
      document.title = `${docName} ${docType}`;
    }

    // TODO: Добавить условие чтобы при возврате назад он проставлял заголовок предыдущей страницы
  }, [docEdo]);

  return (
    <>
      {settings.show_table && settings.row_column_editor.length > 0 && (
        <EdoRowColumnForm
          id={docEdo.data.id}
          row_column_editor={settings.row_column_editor}
          button_editor={settings.button_editor}
          data={docEdo.data.doc_row}
          colleaguesList={colleaguesList}
          departmentList={departmentList}
          handbookList={handbookList}
          isDisabledForm={isAdmin ? !isAdmin : isDisabledForm}
          isDisabledStatus={isDisabledForm}
          isAdmin={isAdmin}
          attrForm={form}
          docData={docEdo.data}
          constant={settings?.constant}
          isDocEditCs={settings?.docAccess?.edit}
        />
      )}
      {docEdo?.data?.doc_order && (
        <CreateOrderDoc
          docCreateAccess={
            docEdo?.data?.doc_order && docEdo?.data?.docOrderCreateAccess
          }
          currentDocId={docEdo?.data?.id}
        />
      )}
      {settings.remark.remark && (
        <EdoRemark
          id={docEdo.data.id}
          settings={settings.remark.remark}
          isDisabledForm={isAdmin ? !isAdmin : isDisabledForm}
          name="remark"
          data={{ remark: docEdo.data.remark }}
          isDisabledStatus={isDisabledForm}
          constant={settings?.constant}
          isDocEditCs={settings?.docAccess?.edit}
        />
      )}
      {settings.remark.remark2 && (
        <EdoRemark
          id={docEdo.data.id}
          settings={settings.remark.remark2}
          isDisabledForm={isAdmin ? !isAdmin : isDisabledForm}
          name="remark2"
          data={{ remark2: docEdo.data.remark2 }}
          isDisabledStatus={isDisabledForm}
        />
      )}
      {docEdo?.data?.class?.constant === 'RecReport' && (
        <EdoMatching form={matchingForm} />
      )}
      {settings.attribute_editor.length > 0 && (
        <EdoAttributeForm
          id={docEdo.data.id}
          attribute_editor={settings.attribute_editor}
          data={docEdo.data.attributes}
          colleaguesList={colleaguesListForAttributes}
          departmentList={departmentList}
          handbookList={handbookList}
          isDisabledForm={isAdmin ? !isAdmin : isDisabledForm}
          form={form}
          isDisabledStatus={isDisabledForm}
          constant={settings.constant}
          isDocEditCs={settings?.docAccess?.edit}
        />
      )}
      {settings.doc_file_editor &&
        Object.keys(settings.doc_file_editor).map((item, index) => {
          if (settings.doc_file_editor[item].show_column) {
            return (
              <EdoUploadForm
                settings={settings.doc_file_editor[item]}
                id={docEdo.data.id}
                files={docEdo.data.doc_file}
                isDisabledForm={isAdmin ? !isAdmin : isDisabledForm}
                key={index}
                form={form}
                isDisabledStatus={isDisabledForm}
              />
            );
          }
        })}

      <AgreementItem
        remarkTemplate={docEdo.data.remark_template}
        documentID={docEdo.data.id}
        docCompanyId={docEdo?.data?.class?.company_id}
        status={docEdo.data?.status?.constant}
        isDisabledForm={isAdmin ? !isAdmin : isDisabledForm}
        classType={docEdo?.data?.class?.constant}
        disabledButton={
          docEdo.data.active !== undefined ? !docEdo.data.active : false
        }
        docAccess={settings?.docAccess}
        isDisabledStatus={isDisabledForm}
        handleActiveCS={() => setActiveAgreementsCS(!activeAgreementsCS)}
      />

      {activeAgreementsCS && (
        <AgreementCS
          class_id={docEdo?.data?.class?.id}
          docEdoId={docEdo?.data?.id}
          attributes={docEdo?.data?.attributes}
          disabledButton={
            docEdo.data.active !== undefined ? !docEdo.data.active : false
          }
        />
      )}
    </>
  );
}

export default EdoDocumentPage;
